/**
 * Library list columns, chosen and ordered by each person and kept on the
 * server: built-in facts (status, duration, format, class, size, added) and
 * any metadata field they may see.
 */
import { useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Columns3, RotateCcw } from 'lucide-react';
import { videoFormatLabel, type AssetSummary, type FieldDefinition, type MetadataSchemaDefinition } from '@prism/contracts';
import { useServerPref } from '../lib/prefs';
import { bytes, duration, relative, resolution } from '../lib/format';
import { AssetStatus } from '../components/Status';
import { toLocalInput } from '../components/MetadataFields';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';
import { groupColumns } from './library/group-filter/GroupFilter';
import { rightsColor } from '@prism/contracts';
import { rightsStateText, rightsTooltip } from '../lib/rights';
import { useRightsSettings } from '../api/hooks/rights';

/** 1.8.0: the Rights column (only while rights management is on): the state in its colour, the details on hover. */
const RIGHTS_COLUMN: Column = {
  id: 'rights', label: 'Rights',
  render: (a) => (a.rights
    ? <span className={`rights-cell ${rightsColor(a.rights)}`} data-testid="rights-column" data-state={a.rights.state} title={rightsTooltip(a.rights)}>{rightsStateText(a.rights)}</span>
    : <span className="faint">—</span>),
};

/** "1080p · 25 fps", "1080i · 25 fps" (field order in the tooltip) or a portrait clip's size (1.0.0-rc.11). */
function VideoFormat({ a }: { a: AssetSummary }) {
  const f = videoFormatLabel(a);
  return <span title={f.title ?? undefined} data-testid="asset-format">{f.label}</span>;
}

export interface Column { id: string; label: string; field?: FieldDefinition; render: (a: AssetSummary) => React.ReactNode; muted?: boolean; mono?: boolean }

const BUILT_IN: Column[] = [
  { id: 'assetId', label: 'Asset ID', mono: true, render: (a) => a.assetCode },
  { id: 'status', label: 'Status', render: (a) => <AssetStatus status={a.status} availability={a.availability} /> },
  { id: 'duration', label: 'Duration', mono: true, render: (a) => duration(a.durationTicks) },
  { id: 'format', label: 'Format', muted: true, render: (a) => (a.mediaKind === 'audio' ? 'Audio' : a.mediaKind === 'graphic' ? `Graphic · ${resolution(a.width, a.height)}` : a.mediaKind === 'image' ? `Still · ${resolution(a.width, a.height)}` : a.height ? <VideoFormat a={a} /> : '—') },
  { id: 'class', label: 'Class', muted: true, render: (a) => (a.category ? <span className="pill category-pill" style={{ borderColor: a.category.color, color: a.category.color }}>{a.category.name}</span> : '—') },
  { id: 'file', label: 'File name', muted: true, render: (a) => a.originalFilename ?? '—' },
  { id: 'size', label: 'Size', muted: true, render: (a) => bytes(a.sizeBytes) },
  { id: 'added', label: 'Added', muted: true, render: (a) => relative(a.createdAt) },
];

function fieldColumn(f: FieldDefinition): Column {
  return {
    id: f.id, label: f.label, field: f,
    render: (a) => {
      const v = a.metadata[f.id];
      if (v == null || v === '') return <span className="faint">—</span>;
      if (f.options) return (Array.isArray(v) ? v : [v]).map((x) => f.options!.find((o) => o.value === x)?.label ?? String(x)).join(', ');
      if (Array.isArray(v)) return v.join(', ');
      if (typeof v === 'boolean') return v ? 'Yes' : 'No';
      if (f.type === 'datetime' && typeof v === 'string') return toLocalInput(v).replace('T', ' ') || v;
      return String(v).replace(/<[^>]+>/g, '').slice(0, 120);
    },
  };
}

export function useLibraryColumns(schema: MetadataSchemaDefinition | undefined) {
  const fields = (schema?.fields ?? []).filter((f) => f.id !== 'f_title' && !f.hidden);
  const rightsOn = Boolean(useRightsSettings().data?.enabled);
  // 1.4.0 (G1): Series, Season and Episode no.
  const all = [...BUILT_IN, ...(rightsOn ? [RIGHTS_COLUMN] : []), ...groupColumns(), ...fields.map(fieldColumn)];
  const defaults = ['status', ...(rightsOn ? ['rights'] : []), 'duration', 'format', ...fields.filter((f) => f.column).map((f) => f.id), 'size', 'added'];

  const [saved, setSaved] = useServerPref<{ columns: string[] } | null>('library.columns', null);
  const ids = (saved?.columns ?? defaults).filter((id) => all.some((c) => c.id === id));
  return { all, shown: ids.map((id) => all.find((c) => c.id === id)!), ids, set: (next: string[] | null) => setSaved(next ? { columns: next } : null), customised: Boolean(saved) };
}

export function ColumnsMenu({ all, ids, set, customised }: { all: Column[]; ids: string[]; set: (ids: string[] | null) => void; customised: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismissOnOutside(ref, open, () => setOpen(false));
  const move = (id: string, by: number) => {
    const i = ids.indexOf(id);
    const next = [...ids];
    next.splice(i, 1);
    next.splice(Math.max(0, Math.min(next.length, i + by)), 0, id);
    set(next);
  };
  const ordered = [...ids.map((id) => all.find((c) => c.id === id)!), ...all.filter((c) => !ids.includes(c.id))];
  return (
    <div className="menu-anchor" ref={ref}>
      <button className="btn ghost icon sm" aria-label="Columns" title="Choose columns" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="columns-menu"><Columns3 size={15} /></button>
      {open && (
        <div className="popover columns-popover" role="dialog" aria-label="Columns">
          <p className="section-label" style={{ margin: '0 0 6px' }}>Columns <span className="faint">(saved for you)</span></p>
          <ul>
            {ordered.map((c) => {
              const on = ids.includes(c.id);
              return (
                <li key={c.id}>
                  <label className="toggle-row"><input type="checkbox" checked={on} onChange={() => set(on ? ids.filter((x) => x !== c.id) : [...ids, c.id])} data-testid={`column-${c.id}`} /><span>{c.label}</span></label>
                  {on && <span className="col-move">
                    <button className="tl-mini" aria-label={`Move ${c.label} left`} disabled={ids.indexOf(c.id) === 0} onClick={() => move(c.id, -1)}><ArrowUp size={11} /></button>
                    <button className="tl-mini" aria-label={`Move ${c.label} right`} disabled={ids.indexOf(c.id) === ids.length - 1} onClick={() => move(c.id, 1)}><ArrowDown size={11} /></button>
                  </span>}
                </li>
              );
            })}
          </ul>
          {customised && <button className="btn ghost sm" onClick={() => set(null)}><RotateCcw size={12} /> Default columns</button>}
        </div>
      )}
    </div>
  );
}
